/**
 * @module Deferred dialog sections: content that renders only after the frame that shows its
 * dialog, so the click that opens a dialog paints the title and actions without first building
 * every field.
 * See docs/developer/ui-latency.md for the measured cost this removes.
 */

import { startTransition, useEffect, useState, type ReactNode } from 'react';
import { afterNextPaint } from '@/lib/afterPaint';

/** Inputs of a deferred section. */
interface AfterFirstPaintProps {
  /** Content rendered once the deferred transition commits. */
  children: ReactNode;
}

/**
 * Render nothing on mount, then `children` in a transition that starts after the next frame has
 * painted (`afterNextPaint` at user-visible priority). The frame that shows the surrounding
 * dialog therefore carries neither the render nor the style and layout of these children. Mounted inside dialog content, it starts empty on every
 * open. Callers must not place anything the first frame needs (the title, focus target, primary
 * actions) inside it.
 */
export function AfterFirstPaint({ children }: AfterFirstPaintProps) {
  const [ready, setReady] = useState(false);
  useEffect(
    () => afterNextPaint(() => startTransition(() => setReady(true)), { priority: 'user-visible' }),
    []
  );
  return ready ? <>{children}</> : null;
}
